import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup,
  Select, TextField,
} from "@mui/material";
import type { Appointment, CreateAppointmentPayload, EditScope, InvoicingSettings, SchedulingSettings } from "./types";
import { fmtHm } from "./time";

export interface PatientOption {
  patient_id: string;
  name: string;
}

type Recurrence = "none" | "weekly" | "biweekly";

interface Props {
  open: boolean;
  /** Editing an existing appointment, or null when creating. */
  appointment: Appointment | null;
  /** Prefill instant for creation (from double-clicked cell or drag start). */
  createAt: Date | null;
  /** Prefill duration for creation (from a drag-selected range), if any. */
  createDurationMin?: number | null;
  isSeriesMember: boolean;
  patients: PatientOption[];
  settings: SchedulingSettings;
  /** null while loading — the invoicing row shows nothing until known. */
  invoicing: InvoicingSettings | null;
  onClose: () => void;
  onCreate: (payload: CreateAppointmentPayload) => void;
  onEdit: (id: string, body: Record<string, unknown>, scope: EditScope) => void;
  onCancelAppt: (id: string, notify: boolean) => void;
  onDelete: (id: string, scope: EditScope, notify: boolean) => void;
  onCreateInvoice: (id: string, grossHuf: number) => void;
  onStornoInvoice: (id: string) => void;
  onOpenInvoicingSettings: () => void;
}

function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const LEADS = [0, 60, 24 * 60, 48 * 60];

export default function AppointmentDialog({
  open, appointment, createAt, createDurationMin, isSeriesMember, patients, settings,
  invoicing, onClose, onCreate, onEdit, onCancelAppt, onDelete,
  onCreateInvoice, onStornoInvoice, onOpenInvoicingSettings,
}: Props) {
  const { t } = useTranslation();
  const editing = appointment !== null;
  const seed = useMemo(
    () => (appointment ? new Date(appointment.start_at) : createAt ?? new Date()),
    [appointment, createAt],
  );

  const [patientId, setPatientId] = useState("");
  const [date, setDate] = useState(toDateInput(seed));
  const [time, setTime] = useState(fmtHm(seed));
  const [duration, setDuration] = useState(settings.default_duration_min);
  const [lead, setLead] = useState(settings.default_reminder_lead_min);
  const [recurrence, setRecurrence] = useState<Recurrence>("none");
  const [untilDate, setUntilDate] = useState("");
  const [notes, setNotes] = useState("");
  const [scope, setScope] = useState<EditScope>("occurrence");
  // Emails are opt-in per action — unchecked on every open, never remembered.
  const [notify, setNotify] = useState(false);
  const [invoiceGross, setInvoiceGross] = useState(0);
  // Storno is destructive-ish: first click arms, second confirms.
  const [stornoArmed, setStornoArmed] = useState(false);
  // Snapshot of the values as loaded — submit sends ONLY changed fields.
  // The backend treats key PRESENCE as change intent (a reproduced start_at
  // would detach a series member and reset reminder state; review M1-#2).
  const [initial, setInitial] = useState<{
    date: string; time: string; duration: number; lead: number; notes: string;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setPatientId(appointment?.patient_id ?? "");
    setDate(toDateInput(seed));
    setTime(fmtHm(seed));
    setDuration(
      appointment
        ? Math.round((new Date(appointment.end_at).getTime() - new Date(appointment.start_at).getTime()) / 60_000)
        : createDurationMin ?? settings.default_duration_min,
    );
    setLead(appointment?.reminder_lead_min ?? settings.default_reminder_lead_min);
    setRecurrence("none");
    setUntilDate("");
    setNotes(appointment?.notes ?? "");
    setScope("occurrence");
    setNotify(false);
    setInvoiceGross(invoicing?.unit_price_huf ?? 0);
    setStornoArmed(false);
    const dur = appointment
      ? Math.round((new Date(appointment.end_at).getTime() - new Date(appointment.start_at).getTime()) / 60_000)
      : createDurationMin ?? settings.default_duration_min;
    setInitial({
      date: toDateInput(seed),
      time: fmtHm(seed),
      duration: dur,
      lead: appointment?.reminder_lead_min ?? settings.default_reminder_lead_min,
      notes: appointment?.notes ?? "",
    });
  }, [open, appointment, seed, settings, createDurationMin, invoicing]);

  const localStart = () => {
    const [y, mo, d] = date.split("-").map(Number);
    const [h, mi] = time.split(":").map(Number);
    return new Date(y, mo - 1, d, h, mi);
  };

  const submit = () => {
    if (editing && appointment) {
      if (scope === "series") {
        onEdit(appointment.appointment_id, {
          local_start_time: time,
          reminder_lead_min: lead,
          duration_min: duration,
          ...(notify ? { notify: true } : {}),
        }, "series");
      } else {
        const body: Record<string, unknown> = {};
        if (initial && (date !== initial.date || time !== initial.time)) {
          body.start_at = localStart().toISOString();
        }
        if (!initial || duration !== initial.duration) body.duration_min = duration;
        if (!initial || lead !== initial.lead) body.reminder_lead_min = lead;
        if (!initial || notes !== initial.notes) body.notes = notes;
        if (Object.keys(body).length === 0) {
          onClose(); // nothing changed — no PATCH, no accidental detach
          return;
        }
        if (notify) body.notify = true;
        onEdit(appointment.appointment_id, body, "occurrence");
      }
      return;
    }
    const payload: CreateAppointmentPayload = {
      patient_id: patientId,
      duration_min: duration,
      reminder_lead_min: lead,
      notes: notes || undefined,
      notify,
    };
    if (recurrence === "none") {
      payload.start_at = localStart().toISOString();
    } else {
      payload.series = {
        anchor_date: date,
        local_start_time: time,
        timezone: settings.timezone,
        interval_weeks: recurrence === "weekly" ? 1 : 2,
        ...(untilDate ? { until: untilDate } : {}),
      };
    }
    onCreate(payload);
  };

  const canSubmit = editing || (patientId && date && time);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {editing ? t("calendar.editAppointment") : t("calendar.newAppointment")}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        {!editing && (
          <FormControl size="small" fullWidth>
            <InputLabel id="cal-patient-label">{t("calendar.patient")}</InputLabel>
            <Select
              labelId="cal-patient-label"
              label={t("calendar.patient")}
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              data-testid="cal-dialog-patient"
            >
              {patients.map((p) => (
                <MenuItem key={p.patient_id} value={p.patient_id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {editing && isSeriesMember && (
          <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as EditScope)}>
            <FormControlLabel value="occurrence" control={<Radio size="small" />}
              label={t("calendar.scopeThis")} />
            <FormControlLabel value="series" control={<Radio size="small" />}
              label={t("calendar.scopeSeries")} />
          </RadioGroup>
        )}

        <TextField size="small" type="date" label={t("calendar.date")}
          value={date} onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }} disabled={editing && scope === "series"} />
        <TextField size="small" type="time" label={t("calendar.time")}
          value={time} onChange={(e) => setTime(e.target.value)}
          InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="number" label={t("calendar.duration")}
          value={duration} onChange={(e) => setDuration(Number(e.target.value))}
          inputProps={{ min: 5, max: 480, step: 5 }} />

        <FormControl size="small" fullWidth>
          <InputLabel id="cal-lead-label">{t("calendar.reminder")}</InputLabel>
          <Select labelId="cal-lead-label" label={t("calendar.reminder")}
            value={lead} onChange={(e) => setLead(Number(e.target.value))}>
            {LEADS.map((v) => (
              <MenuItem key={v} value={v}>{t(`calendar.leads.${v}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {!editing && (
          <>
            <FormControl size="small" fullWidth>
              <InputLabel id="cal-rec-label">{t("calendar.recurrence.label")}</InputLabel>
              <Select labelId="cal-rec-label" label={t("calendar.recurrence.label")}
                value={recurrence} onChange={(e) => setRecurrence(e.target.value as Recurrence)}
                data-testid="cal-dialog-recurrence">
                <MenuItem value="none">{t("calendar.recurrence.none")}</MenuItem>
                <MenuItem value="weekly">{t("calendar.recurrence.weekly")}</MenuItem>
                <MenuItem value="biweekly">{t("calendar.recurrence.biweekly")}</MenuItem>
              </Select>
            </FormControl>
            {recurrence !== "none" && (
              <TextField size="small" type="date" label={t("calendar.recurrence.endBy")}
                value={untilDate} onChange={(e) => setUntilDate(e.target.value)}
                InputLabelProps={{ shrink: true }} />
            )}
          </>
        )}

        <TextField size="small" label={t("calendar.notes")} multiline minRows={2}
          value={notes} onChange={(e) => setNotes(e.target.value)}
          disabled={editing && scope === "series"} />

        <FormControlLabel
          control={
            <Checkbox size="small" checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              data-testid="cal-dialog-notify" />
          }
          label={t("calendar.notifyClient")}
        />

        {editing && appointment && appointment.status !== "cancelled" &&
          new Date(appointment.end_at).getTime() < Date.now() && invoicing && (
          <div className="cal-invoice">
            <span className="cal-invoice__label">{t("calendar.invoicing.label")}</span>
            {appointment.invoice_number ? (
              <div className="cal-invoice__body">
                <span className="cal-invoice__number" data-testid="cal-invoice-number">
                  {appointment.invoice_number}
                  {appointment.invoice_storno_number &&
                    ` · ${t("calendar.invoicing.stornoed")} (${appointment.invoice_storno_number})`}
                </span>
                <span className="cal-invoice__actions">
                  {appointment.invoice_url && (
                    <a className="btn btn--ghost btn--sm" href={appointment.invoice_url}
                      target="_blank" rel="noopener noreferrer">
                      {t("calendar.invoicing.open")} ↗
                    </a>
                  )}
                  {!appointment.invoice_storno_number && (
                    <button type="button"
                      className={`btn btn--sm${stornoArmed ? " btn--danger" : ""}`}
                      data-testid="cal-invoice-storno"
                      onClick={() => {
                        if (!stornoArmed) { setStornoArmed(true); return; }
                        onStornoInvoice(appointment.appointment_id);
                      }}>
                      {stornoArmed ? t("calendar.invoicing.stornoConfirm")
                                   : t("calendar.invoicing.storno")}
                    </button>
                  )}
                </span>
              </div>
            ) : !invoicing.agent_key_set ? (
              <button type="button" className="btn btn--sm"
                data-testid="cal-invoice-setup" onClick={onOpenInvoicingSettings}>
                {t("calendar.invoicing.configure")}
              </button>
            ) : (
              <span className="cal-invoice__actions">
                <TextField size="small" type="number"
                  label={t("calendar.invoicing.amount")}
                  value={invoiceGross || ""}
                  onChange={(e) => setInvoiceGross(Number(e.target.value))}
                  inputProps={{ min: 1, step: 500, "data-testid": "cal-invoice-gross" }}
                  sx={{ width: 140 }} />
                <button type="button" className="btn btn--primary btn--sm"
                  disabled={invoiceGross <= 0}
                  data-testid="cal-invoice-create"
                  onClick={() => onCreateInvoice(appointment.appointment_id, invoiceGross)}>
                  {t("calendar.invoicing.create")}
                </button>
              </span>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {editing && appointment && (
          <>
            <button type="button" className="btn btn--sm"
              onClick={() => onCancelAppt(appointment.appointment_id, notify)}>
              {t("calendar.cancelAppointment")}
            </button>
            <button type="button" className="btn btn--danger btn--sm"
              onClick={() => onDelete(appointment.appointment_id, scope, notify)}>
              {t("common.delete")}
            </button>
          </>
        )}
        <span style={{ marginLeft: "auto" }} />
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
          {t("common.close")}
        </button>
        <button type="button" className="btn btn--primary btn--sm" disabled={!canSubmit}
          onClick={submit} data-testid="cal-dialog-save">
          {t("common.save")}
        </button>
      </DialogActions>
    </Dialog>
  );
}
